// ✅ src/pages/Vehicle/VehicleSubTypeListing.jsx
// ✅ FIXED: light-theme invisible row text + resilient list field mapping
//
// Root cause of the blank table:
//   Row backgrounds were driven by ThemeContext `isDark`,
//   but cell text used Tailwind `dark:` variants.
//   If `html`/`body` has class "dark" while the page skin is light
//   (or the two flags disagree), you get white text on white rows —
//   S.No + names vanish, only checkboxes remain.
//
// Also hardened:
//   - vehicleTypeId query/session keys (vehicleTypeId / vehicletypeid / VehicleTypeID)
//   - list row id/name keys (subtype + type aliases from ASPX / API)
//   - table colors now follow `isDark` only (no mixed `dark:` utilities)

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaSave, FaTrash, FaArrowLeft, FaFilter } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";

const API = "https://nissi-india.com/api/mainSubTypeListing";

const pick = (row, keys, fallback = "") => {
  if (!row || typeof row !== "object") return fallback;

  // exact keys first
  for (const k of keys) {
    const v = row[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }

  // case-insensitive / spaced-key fallback (API / DataTable aliases)
  const map = Object.create(null);
  for (const k of Object.keys(row)) {
    map[String(k).toLowerCase().replace(/[\s_]/g, "")] = row[k];
  }
  for (const k of keys) {
    const v = map[String(k).toLowerCase().replace(/[\s_]/g, "")];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }

  return fallback;
};

export default function VehicleSubTypeListing() {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  const textMain = isDark ? "text-white" : "text-gray-900";
  const textMuted = isDark ? "text-gray-300" : "text-gray-700";
  const rowBg = isDark ? "bg-slate-900" : "bg-white";
  const rowHover = isDark ? "hover:bg-slate-800" : "hover:bg-gray-50";
  const rowBorder = isDark ? "border-slate-700" : "border-gray-200";
  const inputBg = isDark
    ? "bg-gray-700 border-gray-600 text-white"
    : "bg-white border-gray-300 text-gray-900";

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const qs = useMemo(() => new URLSearchParams(window.location.search), []);
  const vehicleTypeId = useMemo(() => {
    const fromQS =
      qs.get("vehicleTypeId") ||
      qs.get("vehicletypeid") ||
      qs.get("VehicleTypeID") ||
      "";
    const fromSS =
      sessionStorage.getItem("vehicleTypeId") ||
      sessionStorage.getItem("vehicletypeid") ||
      sessionStorage.getItem("VehicleTypeID") ||
      "";
    const val = Number(fromQS || fromSS || 0);
    return Number.isFinite(val) ? val : 0;
  }, [qs]);

  const [mainTypeDropdown, setMainTypeDropdown] = useState([]);
  const [groupingDropdown, setGroupingDropdown] = useState([]);
  const [vehicleTypeIndex, setVehicleTypeIndex] = useState("");

  const [selectedMainType, setSelectedMainType] = useState("");
  const [selectedSubTypeToGroup, setSelectedSubTypeToGroup] = useState("Select");

  const [subTypeCount, setSubTypeCount] = useState("");
  const [subTypeTooltip, setSubTypeTooltip] = useState("");

  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const allChecked = useMemo(
    () => rows.length > 0 && selectedIds.length === rows.length,
    [rows, selectedIds]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  const getRowId = (r) =>
    Number(
      pick(
        r,
        [
          "fld_vehiclesubtypeid",
          "fld_subtypeid",
          "fld_vehicletypeid",
          "VehicleSubTypeID",
          "SubTypeID",
          "VehicleTypeID",
          "subTypeId",
          "subtypeid",
          "vehicletypeid",
          "id",
        ],
        0
      )
    ) || 0;

  const getRowName = (r) =>
    pick(
      r,
      [
        "fld_vehiclesubtypename",
        "fld_vehicletypename",
        "fld_subtypename",
        "Vehicle Sub Type",
        "VehicleSubType",
        "Vehicle Type",
        "VehicleType",
        "SubTypeName",
        "subTypeName",
        "name",
        "Text",
      ],
      "-"
    );

  const unwrapList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.rows)) return payload.rows;
    if (Array.isArray(payload?.list)) return payload.list;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
    if (Array.isArray(payload?.data?.list)) return payload.data.list;
    return [];
  };

  const fetchInit = async () => {
    if (!vehicleTypeId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/init`, {
        params: { vehicleTypeId },
        headers: commonHeaders,
      });

      if (data?.success === false) throw new Error(data?.message || "Init failed");

      const d = data?.data || {};
      setMainTypeDropdown(d.mainTypeDropdown || d.MainTypeDropdown || []);
      setGroupingDropdown(d.groupingDropdown || d.GroupingDropdown || []);
      setVehicleTypeIndex(d.vehicleTypeIndex ?? d.VehicleTypeIndex ?? "");
      setSelectedMainType(String(vehicleTypeId));
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e?.message || "Init error");
    } finally {
      setLoading(false);
    }
  };

  const fetchList = async () => {
    if (!vehicleTypeId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/list`, {
        params: { vehicleTypeId },
        headers: commonHeaders,
      });

      if (data?.success === false) throw new Error(data?.message || "List failed");

      const list = unwrapList(data);
      setRows(list);
      setSelectedIds([]);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e?.message || "Load error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubTypeInfo = async (subTypeId) => {
    if (!subTypeId || subTypeId === "Select") {
      setSubTypeCount("");
      setSubTypeTooltip("");
      return;
    }

    try {
      const { data } = await axios.get(`${API}/subtype-info`, {
        params: { subTypeId },
        headers: commonHeaders,
      });

      if (data?.success === false) throw new Error(data?.message || "Subtype info failed");

      const info = data?.data || {};
      setSubTypeCount(String(info.count ?? info.Count ?? ""));
      setSubTypeTooltip(String(info.tooltip ?? info.Tooltip ?? ""));
    } catch (e) {
      console.error(e);
      setSubTypeCount("");
      setSubTypeTooltip("");
    }
  };

  const handleSaveGroup = async () => {
    if (!vehicleTypeId) {
      toast.error("VehicleTypeID missing");
      return;
    }

    if (selectedSubTypeToGroup === "Select") {
      toast.warning("Please select a subtype to group");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/save-group`,
        {
          vehicleTypeId,
          selectedSubTypeId: Number(selectedSubTypeToGroup),
        },
        { headers: commonHeaders }
      );

      if (data?.success === false) throw new Error(data?.message || "Save failed");

      toast.success(data?.message || "Saved successfully");
      setSelectedSubTypeToGroup("Select");
      setSubTypeCount("");
      setSubTypeTooltip("");

      await fetchList();
      await fetchInit();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSelected = async () => {
    if (!selectedIds.length) {
      toast.warning("Select at least one row to remove");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/remove`,
        { removeIds: selectedIds },
        { headers: commonHeaders }
      );

      if (data?.success === false) throw new Error(data?.message || "Remove failed");

      toast.success(data?.message || "Removed successfully");
      await fetchList();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e?.message || "Remove failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!vehicleTypeId) {
      toast.error(
        "VehicleTypeID missing. Use ?vehicleTypeId=1 or set sessionStorage VehicleTypeID"
      );
      return;
    }

    fetchInit();
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleTypeId, commonHeaders]);

  const toggleSelectAll = () => {
    if (allChecked) setSelectedIds([]);
    else setSelectedIds(rows.map(getRowId).filter(Boolean));
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <VehicleLayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">VEHICLE TYPE WITH SUB TYPE</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <h1 className={`text-2xl font-bold ${textMain}`}>
              Vehicle Type With Sub Type
            </h1>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
              }`}
            >
              {rows.length} Records
            </span>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setShowFilters((s) => !s)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800" +
                    (showFilters ? " bg-gray-800" : "")
                  : "border-gray-300 text-gray-700 hover:bg-gray-50" +
                    (showFilters ? " bg-gray-50" : "")
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            <button
              onClick={() => navigate(-1)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FaArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
        </div>

        {showFilters && (
          <div
            className={`rounded-xl p-6 mb-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${textMuted}`}>
                  Vehicle Main Type
                </label>
                <select value={selectedMainType} disabled className={`w-full px-3 py-2 border rounded-lg ${inputBg}`}>
                  {(mainTypeDropdown || []).map((o, i) => {
                    const id = pick(o, ["fld_vehicletypeid", "Value", "id"], "");
                    const name = pick(o, ["fld_vehicletypename", "Text", "name"], "");
                    return (
                      <option key={`${id}-${i}`} value={id}>
                        {name}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${textMuted}`}>
                  Select Sub Type To Group
                </label>
                <select
                  value={selectedSubTypeToGroup}
                  onChange={(e) => {
                    setSelectedSubTypeToGroup(e.target.value);
                    fetchSubTypeInfo(e.target.value);
                  }}
                  className={`w-full px-3 py-2 border rounded-lg ${inputBg}`}
                >
                  <option value="Select">Select</option>
                  {(groupingDropdown || []).map((o, i) => {
                    const id = pick(o, [
                      "fld_vehiclesubtypeid",
                      "fld_vehicletypeid",
                      "Value",
                      "id",
                    ], "");
                    const name = pick(o, [
                      "fld_vehiclesubtypename",
                      "fld_vehicletypename",
                      "Text",
                      "name",
                    ], "");
                    return (
                      <option key={`${id}-${i}`} value={id}>
                        {name}
                      </option>
                    );
                  })}
                </select>

                {selectedSubTypeToGroup !== "Select" && (
                  <div className="mt-2 text-xs">
                    <div className={textMuted}>
                      Vehicles Count: <b>{subTypeCount || "0"}</b>
                    </div>
                    {subTypeTooltip ? (
                      <div
                        className={`mt-1 p-2 rounded border whitespace-pre-line ${
                          isDark ? "border-gray-600 bg-gray-900/40" : "border-gray-200 bg-white"
                        }`}
                        title={subTypeTooltip}
                      >
                        {subTypeTooltip}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${textMuted}`}>
                  Vehicle Type Index
                </label>
                <input
                  value={vehicleTypeIndex}
                  disabled
                  className={`w-full px-3 py-2 border rounded-lg ${inputBg}`}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={handleSaveGroup}
                disabled={loading || selectedSubTypeToGroup === "Select"}
                className={`flex items-center gap-2 px-6 py-3 text-white rounded-lg font-semibold shadow-lg ${
                  loading || selectedSubTypeToGroup === "Select"
                    ? "bg-gray-400 cursor-not-allowed"
                    : `${buttonClass} ${hoverClass}`
                }`}
              >
                <FaSave />
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        )}

        {selectedIds.length > 0 && (
          <div
            className={`rounded-lg p-4 mb-6 ${
              isDark ? "bg-blue-900/30 border border-blue-700" : "bg-blue-50 border border-blue-200"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className={`font-semibold ${isDark ? "text-blue-200" : "text-blue-800"}`}>
                {selectedIds.length} item(s) selected
              </div>
              <button
                onClick={handleRemoveSelected}
                disabled={loading}
                className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium ${
                  loading ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"
                }`}
              >
                <FaTrash />
                Remove Selected
              </button>
            </div>
          </div>
        )}

        <div className={`rounded-xl shadow-sm border overflow-hidden ${rowBorder}`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`${buttonClass} text-white`}>
                <tr>
                  <th className="px-4 py-4 text-left font-semibold w-16">S.No</th>
                  <th className="px-4 py-4 text-left font-semibold">Vehicle Sub Type</th>
                  <th className="px-4 py-4 text-center font-semibold w-40">
                    <label className="inline-flex items-center justify-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={allChecked}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 accent-white cursor-pointer"
                      />
                      Select All
                    </label>
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr className={rowBg}>
                    <td colSpan={3} className={`px-4 py-10 text-center ${textMuted}`}>
                      Loading...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr className={rowBg}>
                    <td colSpan={3} className={`px-4 py-10 text-center ${textMuted}`}>
                      No records
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => {
                    const id = getRowId(r);
                    const name = getRowName(r);
                    const tooltip = pick(r, ["_tooltip", "tooltip", "Tooltip"], "");

                    return (
                      <tr
                        key={id || `row-${idx}`}
                        title={tooltip}
                        className={`${rowBg} ${rowHover} transition-colors duration-200 border-b ${rowBorder}`}
                      >
                        <td className={`px-4 py-3 text-sm font-medium ${textMain}`}>
                          {idx + 1}
                        </td>
                        <td className={`px-4 py-3 text-sm ${textMain}`}>{name}</td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(id)}
                            onChange={() => toggleOne(id)}
                            className={`h-4 w-4 rounded cursor-pointer ${
                              isDark ? "accent-emerald-400" : "accent-emerald-600"
                            }`}
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          closeOnClick
          pauseOnHover
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </VehicleLayoutWrapper>
  );
}